import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * Iter v34b0 — Client Cart Context (Iter v34b3: per-user isolation).
 * State: items[{ id, material_id, name, image_url, unit, category, variants, price_snapshot, qty, selected_loc, tax_mode, use_bongkar }]
 * Persist to localStorage("client_cart_v1_{userId}") — each account has its own cart bucket.
 * When user logs out or switches account, cart auto-resets to that account's bucket.
 */

const STORAGE_PREFIX = "client_cart_v1_";
const CartCtx = createContext(null);

const genId = () => `cart_${Math.random().toString(36).slice(2, 10)}`;

const readCart = (userId) => {
  if (!userId) return [];
  try {
    const s = localStorage.getItem(STORAGE_PREFIX + userId);
    return s ? JSON.parse(s) : [];
  } catch { return []; }
};

const writeCart = (userId, items) => {
  if (!userId) return;
  try { localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(items)); } catch { /* noop */ }
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [items, setItems] = useState(() => readCart(userId));

  // Iter v34b3 — One-time migration: clear old global cart key (was shared across accounts).
  useEffect(() => {
    try { localStorage.removeItem("client_cart_v1"); } catch { /* noop */ }
  }, []);

  // Iter v34b3 — Re-hydrate cart when user changes (login/logout/switch account).
  useEffect(() => {
    setItems(readCart(userId));
  }, [userId]);

  // Persist current items to per-user bucket
  useEffect(() => {
    writeCart(userId, items);
  }, [items, userId]);

  // Iter v34cu (#1) — Re-sync harga dari katalog live.
  // Keranjang disimpan di localStorage per akun, termasuk SNAPSHOT `variants`.
  // Bila harga di Daftar Harga berubah, snapshot lama membuat tiap akun melihat
  // harga berbeda. Sinkronisasi ini menyamakan harga keranjang dengan katalog.
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    api.get("/catalog").then((r) => {
      if (!alive) return;
      const byMaterial = new Map();
      (r.data || []).forEach((p) => {
        const k = (p.material_name || "").toLowerCase().trim();
        if (!byMaterial.has(k)) byMaterial.set(k, []);
        byMaterial.get(k).push(p);
      });
      setItems((prev) => {
        if (prev.length === 0) return prev;
        let changed = false;
        const next = prev.map((it) => {
          const fresh = byMaterial.get((it.material_id || "").toLowerCase().trim());
          if (!fresh || fresh.length === 0) return it;
          const prevLoc = ((it.variants || [])[it.selected_loc] || {}).delivery_location || "";
          let idx = fresh.findIndex((v) => (v.delivery_location || "") === prevLoc);
          if (idx < 0) idx = 0;
          const same = JSON.stringify(it.variants || []) === JSON.stringify(fresh) && it.selected_loc === idx;
          if (same) return it;
          changed = true;
          return {
            ...it,
            name: fresh[0].material_name || it.name,
            unit: fresh[0].unit || it.unit,
            category: fresh[0].category || it.category,
            variants: fresh,
            selected_loc: idx,
          };
        });
        return changed ? next : prev;
      });
    }).catch(() => { /* offline / no access — keep snapshot */ });
    return () => { alive = false; };
  }, [userId]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      // If same material already in cart → increment qty
      const idx = prev.findIndex((it) => it.material_id === product.material_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (Number(next[idx].qty) || 0) + qty };
        return next;
      }
      // Default: first variant location, with_vat=true, bongkar=false
      const firstVar = (product.variants || [])[0] || {};
      return [...prev, {
        id: genId(),
        material_id: product.material_id,
        name: product.name,
        image_url: product.image_url,
        // Iter v34b5 — image_variant_id untuk lazy-load via /prices/{id}/image
        image_variant_id: product.image_variant_id || null,
        unit: product.unit || "unit",
        category: product.category,
        variants: product.variants || [],
        qty: Math.max(1, qty),
        selected_loc: 0,
        tax_mode: (firstVar.default_vat_mode || "with_vat"),  // "with_vat" | "non_vat"
        use_bongkar: true,
      }];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, qty: Math.max(1, Number(qty) || 1) } : it));
  }, []);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => it.id === id ? { ...it, ...patch } : it));
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
  const uniqueCount = items.length;

  return (
    <CartCtx.Provider value={{ items, addItem, updateQty, updateItem, removeItem, clear, count, uniqueCount }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  const v = useContext(CartCtx);
  if (!v) throw new Error("useCart must be inside CartProvider");
  return v;
}
